/**
 * Shared tuning constants for the stress-majorization layout.
 *
 * Both the solver (`stressLayout.ts`) and the debug diagnostics
 * (`diagnostics.ts`) must compute forces with identical parameters, or
 * the debug view will display stale / off-by-N values while the solver
 * uses the live ones. Keeping them here is the single source of truth.
 *
 * Tune freely — HMR picks up changes, and both files stay in lock step.
 */

export const NODE_W = 220;
export const NODE_H = 140;

// Per-edge ideal gap (ellipse edge-to-edge, in pixels), by the minimum
// degree of the two endpoints. Leaves tuck tight; busy hubs need room to
// fan out their other connections — but only moderately so: hub-hub
// ideals a dense mesh can't satisfy don't buy space, they inflate every
// shortest-path stress target routed THROUGH the mesh, stretching the
// periphery (sinks and shelves drift far off) while the core compresses
// to the separation floor anyway. Density pads (CROWD_*) are the honest
// "more arrows, more space" mechanism on top of this flatter base.
export const IDEAL_BY_MIN_DEGREE: readonly number[] = [60, 60, 60, 90, 120, 160];
export const IDEAL_CAP = 200;
export const IDEAL_TEAM_BONUS = 30;

export const W_CONN = 1.0;
export const W_FLOW = 18;
export const W_ALIGN = 0.04;
export const W_PARENT = 0.0005;
export const W_PORT = 0;

// Two-tier Coulomb repulsion: strong short-range (every pair),
// weak medium-range (unrelated pairs only — skips connected and sibling).
// Strong tier is sized so the force at SEP_GAP (~24px) comfortably beats
// typical spring pulls — the separation guarantee is thereby part of the
// FORCE equilibrium (the hard separation pass is a rarely-firing safety
// net, not a source of post-solve residual forces).
export const W_REPULSE_STRONG = 100000;
export const REPULSE_STRONG_CUTOFF = 40;
export const REPULSE_STRONG_FLOOR = 1;
export const W_REPULSE_WEAK = 100000;
// 375: the long-range weak push is the DOMAIN-SEPARATION force — unrelated
// nodes shove apart up to this range while connected ones don't, so
// districts emerge with visible voids between them (the era-R3 reference
// render). A 300 trial improved sink-stretch/crossing METRICS and made the
// real board visually worse (core carpeted, no voids) — reverted on the
// reference render; metrics advise, renders decide.
export const REPULSE_WEAK_CUTOFF = 375;
export const REPULSE_WEAK_FLOOR = 100;

// Sand-mode friction. Applied per node in stepGradient against the
// summed gradient magnitude (gradient units, same as W_FLOW / W_PORT).
// A node only moves when |∇E| > SAND_F_STATIC; once moving, SAND_F_KINETIC
// is subtracted along the motion direction (kinetic friction). Pinned
// nodes — including the user-dragged node, which enters `pinned` for the
// duration of the drag — bypass friction (drag is 1:1 with pointer).
// Values picked empirically; tune by feel.
export const SAND_F_STATIC = 20;
export const SAND_F_KINETIC = 12;
// Idle-hold variant: same-topology warm re-solves must preserve the
// organize afterpass's crystallized columns, whose spring residuals exceed
// SAND_F_STATIC by design. Non-drag sand solves use this stronger static
// threshold; drag keeps the tuned SAND_F_STATIC feel.
export const SAND_F_STATIC_HOLD = 60;

// Hard overlap-removal phase (separation.ts) — the terminal solver phase.
// SEP_GAP is the guaranteed minimum rect-to-rect separation in the final
// layout; SEP_MAX_PASSES bounds the iterative stage before the guaranteed
// re-placement fallback kicks in.
export const SEP_GAP = 29;
// Crowd-scaled separation floor: per-NODE extra guaranteed clearance,
// px per crowd unit above CROWD_BASE, capped. Pair floor = SEP_GAP +
// pad(a) + pad(b). From the user's manual reference (loop round 1): the
// dense core wants ~73px NN gaps; spring pads can't deliver a FLOOR.
export const SEP_PER_CROWD = 6;
export const SEP_CROWD_MAX = 56;
export const SEP_MAX_PASSES = 60;

// Organize afterpass (organize.ts) — soft column/layer crystallization on
// non-drag solves. Column candidates = x-clusters of current centers,
// refined by local ranks; a member snaps to its column's mean x unless
// that would move it more than COLUMN_SNAP_MAX (it straddles layers on
// purpose). Stacks respace vertically at ORGANIZE_GAP_Y. The pass is
// self-validating: components it would make worse (rings, dense meshes)
// revert to their organic shape.
// Upper bound on organize sweeps — the loop exits early at its fixed
// point (a sweep that moves nothing), which maintenance re-solves rely on.
export const ORGANIZE_MAX_SWEEPS = 6;
// X-cluster split: consecutive sorted x-centers farther apart than this
// start a new column candidate.
export const COLUMN_SPLIT_GAP = 172;
export const COLUMN_SNAP_MAX = 240;
// Edge-less (sibling-only) columns snap only within this band — structure
// may tidy what physics nearly aligned, never impose a ruler line on
// satellites whose equilibria genuinely differ.
export const COLUMN_SNAP_SIBLING_MAX = 48;
export const ORGANIZE_GAP_Y = 40;
// Minimum edge-to-edge clearance between consecutive column silhouettes —
// comfortably above SEP_GAP so the terminal separation pass never needs to
// move a snapped member.
export const COLUMN_MIN_GAP = 60;
// Siblings (pairs sharing a peer) feel weak repulsion at this fraction —
// not exempt: springs own connected pairs, but NOTHING spaced siblings,
// and a dense mesh core (where almost every pair is a sibling) collapsed
// into a carpet with zero spacing force. Physics first; structure aligns.
export const SIBLING_REPULSE_SCALE = 0.35;
// Satellite dock: a degree-1 node snaps to its only peer's dominant axis
// when within this range — a single-connection card sitting slightly off
// its peer's column reads as an accident (era-R3 reference nitpick).
export const SATELLITE_DOCK_MAX = 220;
// ...but only when one axis clearly dominates (off-axis ≤ 0.6 × main):
// near the diagonal the axis choice is a coin flip and a wrong snap
// breaks a chain's row reading.
export const SATELLITE_DOCK_DOMINANCE = 0.6;
// Consecutive columns whose mean x-centers sit closer than this merge into
// ONE column — local ranks over-stratify; the stress geometry disposes.
export const COLUMN_MERGE_BELOW = 105;

// Frame groups (forced partition — `group by` views). A group's frame rect
// is its member bbox inflated by FRAME_PAD per side plus FRAME_LABEL_H on
// top (label strip). The frame-separation phase enforces rect disjointness
// and evicts strangers; cohesion congeals members during the main rounds.
export const FRAME_PAD = 18;
export const FRAME_LABEL_H = 24;
// Must COMPETE with connection springs (k = W_CONN/√(dA·dB) ≤ 1): grouping
// is a hard visual promise — a member parked with its edge-peers instead of
// its group bloats the frame until it swallows strangers. Springs still pull
// whole frames toward their cross-frame peers (macro reading survives).
export const W_FRAME_COHESION = 0.35;
// Nested `group by`: a member also feels each ANCESTOR group's centroid,
// decayed per level — outer families congeal without out-muscling the leaf.
export const FRAME_ANCESTOR_COHESION_DECAY = 0.4;
export const FRAME_SEP_MAX_PASSES = 24;

// Flow separation for the SGD stress phase: the one-sided x-ordering
// constraint per non-feedback directed edge asks for
// FLOW_SEP_FRAC × (edge ideal center-to-center length) of horizontal
// separation. Below 1.0 so the constraint stays compatible with the
// stress target (an edge can satisfy both by sloping ≤ ~45°).
export const FLOW_SEP_FRAC = 0.9;

// Pair assemblies (1-leaf wings) attach only SATELLITES: a leaf whose
// area is at most this fraction of the default card. A full-size service
// hanging off a single caller stays organic — templating it under the
// caller makes majors read as appendages and uniform pair-grids emerge.
// Multi-leaf fans are exempt (a fan of services is a real formation).
export const PAIR_LEAF_MAX_AREA_FRAC = 0.75;

// Density-adaptive spacing — THE RULE: the more arrows, the more space.
// A node's crowd = MESH-NESS (edges among its neighbors — arrows that
// compete for the same area) PLUS its own arrow load (degree above
// CROWD_DEG_BASE — hubs are dense in arrows). Topological, so re-solves
// land on identical pads. Edges/stacks whose mean crowd exceeds CROWD_BASE
// stretch by DENSITY_IDEAL_PER_CROWD px per unit, capped at
// DENSITY_IDEAL_MAX.
export const CROWD_BASE = 6;
export const CROWD_DEG_BASE = 4;
export const DENSITY_IDEAL_PER_CROWD = 12;
// 180 (was 100): the user's manual reference layout (loop round 1) doubled
// the dense core's breathing room (mean NN gap 32→73px) at only +9% median
// edge length — the old cap truncated exactly the pads the max-crowd rule
// earns in a mesh. "More space in connection-dense areas wins."
export const DENSITY_IDEAL_MAX = 180;
